import { Alert, AlertTitle, Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import { FileRecord } from "@/shared/domain/files";
import { useFileTablePreferences } from "@/shared/domain/users/hooks";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { SearchQueryBody } from "@/types";

import { FileMetadataAndTopics } from "../FileMetadataAndTopics";
import { RobotoDataGrid } from "../RobotoDataGrid";

import { ColumnsModal } from "./ColumnsModal";
import { TablePageState, tablePageReducer } from "./stateReducer";
import { createRows, retrieveMetadataColumns } from "./tableLogic";
import type { BaseTableProps } from "./tableProps";

const possibleStandardFilesColumns = [
  "association_id",
  "file_id",
  "modified",
  "modified_by",
  "relative_path",
  "size",
  "org_id",
  "status",
  "upload_id",
  "origination",
  "created",
  "created_by",
  "tags",
  "device_id",
  "source",
  "name",
];

const sortableColumns = new Set<string>();

const initialQuery: SearchQueryBody = {
  sort_by: "created",
  sort_direction: "DESC",
};

const createTableState = (
  columns: string[],
  rowsPerPage: number = 25,
): TablePageState => ({
  modalOpen: false,
  rightClickedRow: null,
  sidebar: { isOpen: false, dataset: null },
  columns,
  rowsPerPage: rowsPerPage as 10 | 25 | 50,
  snackbar: { isOpen: false, message: "" },
  sortColumnIndex: 1,
  sortOrder: "descending",
  lastSearchQuery: initialQuery,
  selectedRows: new Set<string>(),
  preferencesLoaded: false,
});

interface FilesTableProps extends BaseTableProps<FileRecord> {
  selectedRows?: Set<string>;
  setSelectedRows?: (arg: Set<string>) => void;
  onRowSingleClick?: (dataset: FileRecord) => void;
  variant?: "elevation" | "outlined";
  disableSelection?: boolean;
}

export const FilesTable: React.FC<FilesTableProps> = ({
  data: files,
  dataCount,
  loading,
  selectedRows,
  setSelectedRows = () => {},
  onRowSingleClick = () => {},
  variant,
  containerStyle,
  disableSelection,
  fetchMoreData,
}) => {
  const theme = useTheme();
  const {
    preferences,
    saveColumns,
    saveRowsPerPage,
    isLoading: isLoadingPreferences,
    error,
    clearError,
  } = useFileTablePreferences();

  const [state, dispatch] = React.useReducer(
    tablePageReducer,
    createTableState(preferences.columns, preferences.rowsPerPage),
  );

  const [page, setPage] = React.useState(0);

  const filesById = new Map<string, FileRecord>(
    files.map((file) => {
      return [file.file_id, file];
    }),
  );

  const { goto } = useNavigation();

  const [selectedRowsToggle, setSelectedRowsToggle] =
    React.useState<boolean>(false);

  const pageData = React.useMemo(() => {
    return files.slice(
      page * state.rowsPerPage,
      page * state.rowsPerPage + state.rowsPerPage,
    );
  }, [files, state.rowsPerPage, page]);

  const { rows, allPossibleColumns } = React.useMemo(() => {
    const possibleMetadataColumns: string[] =
      retrieveMetadataColumns(pageData) ?? [];

    const rows = createRows(pageData, state.columns, possibleMetadataColumns);

    const allPossibleColumns = possibleStandardFilesColumns
      .concat(possibleMetadataColumns)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    return { rows, allPossibleColumns };
  }, [pageData, state.columns]);

  const noSearchResults =
    Object.keys(state.lastSearchQuery).length > 0 && files.length === 0;

  React.useEffect(() => {
    if (!isLoadingPreferences && !state.preferencesLoaded) {
      dispatch({
        type: "LOAD_PREFERENCES",
        payload: {
          columns: preferences.columns,
          rowsPerPage: preferences.rowsPerPage as 10 | 25 | 50,
        },
      });
    }
  }, [
    isLoadingPreferences,
    preferences.columns,
    preferences.rowsPerPage,
    state.preferencesLoaded,
  ]);

  React.useEffect(() => {
    if (selectedRows && selectedRows.size === 0) {
      setSelectedRowsToggle((prevToggle) => !prevToggle);
      dispatch({
        type: "SET_SELECTED_ROWS",
        payload: { selectedRows: new Set<string>() },
      });
    }
  }, [selectedRows, dispatch]);

  if (!state.preferencesLoaded) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <Paper
          sx={{
            mt: theme.spacing(2),
          }}
          variant={variant}
        >
          {error && (
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ mb: theme.spacing(2) }}
            >
              {error instanceof RobotoDomainException
                ? error.message
                : "An unexpected error occurred while saving your preferences. Please try again later."}
            </Alert>
          )}

          {noSearchResults && (
            <Alert severity="info">
              <AlertTitle>Files</AlertTitle>
              No files match your search criteria. Try changing your search
              terms and try again.
            </Alert>
          )}

          {!noSearchResults && (
            <>
              <RobotoDataGrid
                sortableColumns={sortableColumns}
                sortingOrder={state.sortOrder}
                sortingColumnIndex={state.sortColumnIndex}
                page={page}
                rowsPerPage={state.rowsPerPage}
                nextPageButtonDisabled={
                  dataCount !== undefined
                    ? (page + 1) * state.rowsPerPage >= dataCount
                    : true
                }
                containerStyle={containerStyle}
                loading={loading}
                columnNames={state.columns}
                rows={rows}
                totalRowCount={dataCount}
                onPageChange={(newPage, rowsPerPage) => {
                  if ((newPage + 2) * rowsPerPage >= files.length) {
                    // Request more pages of data from the backend
                    // as we approach the last page of cached results.
                    // 2 pages prior seemed to work well experimentally.
                    fetchMoreData?.();
                  }
                  setPage(newPage);
                }}
                onRowsPerPageChange={(_page, newRowsPerPage) => {
                  setPage(0);
                  dispatch({
                    type: "SET_TABLE_CONFIG",
                    payload: {
                      rowsPerPage: newRowsPerPage,
                    },
                  });

                  void saveRowsPerPage(newRowsPerPage);
                }}
                onColumnSortClicked={() => {
                  // do nothing
                }}
                onAddColumnClick={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: true });
                }}
                onRowDoubleClick={(rowId) => {
                  goto.file({ fileId: rowId });
                }}
                onRowSingleClick={(rowId: string) => {
                  const fileRecord = filesById.get(rowId);
                  if (onRowSingleClick && fileRecord) {
                    onRowSingleClick(fileRecord);
                  }
                }}
                onSelectedRowsChange={(selectedRows) => {
                  setSelectedRows(selectedRows);
                  dispatch({
                    type: "SET_SELECTED_ROWS",
                    payload: { selectedRows: selectedRows },
                  });
                }}
                isRowSelectable={!disableSelection}
                isRowExpandable={true}
                clearSelectedRowsToggle={selectedRowsToggle}
                expandableContent={(rowId: string) => {
                  const fileRecord = filesById.get(rowId);
                  if (fileRecord) {
                    return (
                      <FileMetadataAndTopics
                        fileId={fileRecord.file_id}
                        fileVersion={fileRecord.version}
                        readonly={true}
                      />
                    );
                  }
                }}
              />
              <ColumnsModal
                open={state.modalOpen}
                handleClose={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: false });
                }}
                columns={allPossibleColumns}
                currentColumns={state.columns}
                onSave={(updatedColumns) => {
                  dispatch({ type: "SAVE_COLUMNS", payload: updatedColumns });
                  void saveColumns(updatedColumns);
                }}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
